/* eslint-disable react/no-multi-comp */
import React, { useState } from "react"
import { useInView } from "react-intersection-observer"
import Img from "gatsby-image/withIEPolyfill"
import { graphql } from "gatsby"
import { css, formatDate, formatSlug } from "@jsUtils"

import { Analytics, CTA, Headline, Meta, MediaArticle } from "@shared"
import { Footer, GetInTouch, Header, PageSection } from "@components/sections"
import { CONSTANTS, CONTENT } from "@data"

import styles from "@pageStyles/newsroom.module.scss"

const { COLORS, THEMES, PAGE_TRACKER_KEYS, PAGE_TRACKER_VALUES } = CONSTANTS
const { NEWSROOM } = CONTENT

const TrackedLink = Analytics("a")

const CATEGORIES = {
  press: "press release",
  media: "media coverage"
}

const PAGINATION_INITIAL = {
  press: 3,
  media: 4
}

const PAGINATION_INCREMENT = {
  press: 12,
  media: 4
}

const PressArticle = ({ article }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  })

  return (
    <TrackedLink
      adata="NEWSROOM.READMORE"
      ascope="CTAS"
      className={css(styles.pressArticle, inView && styles.inView)}
      href={`/${formatSlug(article.category)}/${article.slug}`}
      ref={ref}
    >
      <div className={styles.pressTop}>
        {article.thumbnail?.fluid && (
          <Img
            alt={article.thumbnail?.title}
            className={styles.pressImage}
            fluid={article.thumbnail?.fluid}
          />
        )}
      </div>

      <div className={styles.pressBottom}>
        <p className={styles.pressTitle}>{article.title}</p>
        <p className={styles.pressDate}>{formatDate(article.date)}</p>
      </div>
    </TrackedLink>
  )
}

const PressFeatured = ({ article }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  })

  return (
    <div className={css(styles.pressFeatured, inView && styles.inView)} ref={ref}>
      {article.thumbnail?.fluid && (
        <TrackedLink
          adata="NEWSROOM.READMORE"
          ascope="CTAS"
          className={styles.pressImageWrap}
          href={`/${formatSlug(article.category)}/${article.slug}`}
        >
          <Img
            alt={article.thumbnail?.title}
            className={styles.pressImage}
            fluid={article.thumbnail?.fluid}
          />
        </TrackedLink>
      )}

      <div className={styles.pressContent}>
        <div className={styles.pressBar}>
          <p className={styles.pressDate}>{formatDate(article.date)}</p>
          <p className={styles.pressLabel}>Featured</p>
        </div>

        <TrackedLink
          adata="NEWSROOM.READMORE"
          ascope="CTAS"
          className={styles.pressTitle}
          href={`/${formatSlug(article.category)}/${article.slug}`}
        >
          {article.title}
        </TrackedLink>

        <CTA
          adata="NEWSROOM.READMORE"
          ascope="CTAS"
          className={css(styles.pressCta)}
          color={COLORS.FLURO}
          to={`/${formatSlug(article.category)}/${article.slug}`}
        >
          Read More
        </CTA>
      </div>
    </div>
  )
}

const PressArticles = ({ items, pagination }) => {
  // clone items to prevent directly mutating a prop
  let articles = items.slice()

  // catch no press releases
  if (articles.length === 0) {
    return (
      <Headline className={styles.pressEmpty} size={6}>
        There&apos;s nothing here yet. Check back soon.
      </Headline>
    )
  }

  // handle featured article
  // it should appear regardless of pagination
  let featured = null

  const featuredIndex = articles.findIndex(article => article.featuredArticle === true)
  const hasFeatured = featuredIndex !== -1

  if (hasFeatured) {
    featured = articles[featuredIndex]
    articles = articles.filter((_, index) => index !== featuredIndex)
  }

  // apply pagination
  // array index is 0-based, so add 1 to it before comparing it to the pagination limit
  articles = articles.filter((_, index) => index + 1 <= pagination)

  return (
    <div className={css(styles.pressArticles, hasFeatured && styles.hasFeatured)}>
      {hasFeatured && <PressFeatured article={featured} />}
      {articles.map(article => (
        <PressArticle article={article} key={article.id} />
      ))}
    </div>
  )
}

const MediaArticles = ({ items, pagination }) => {
  // clone items to prevent directly mutating a prop
  let articles = items.slice()

  // catch no media coverage
  if (articles.length === 0) {
    return (
      <Headline className={styles.mediaEmpty} size={6}>
        There&apos;s nothing here yet. Check back soon.
      </Headline>
    )
  }

  // apply pagination
  // array index is 0-based, so add 1 to it before comparing it to the pagination limit
  articles = articles.filter((_, index) => index + 1 <= pagination)

  return (
    <div className={styles.mediaArticles}>
      {articles.map(article => (
        <MediaArticle article={article} key={article.id} />
      ))}
    </div>
  )
}

const Pagination = ({ increment, items, pagination, setPagination }) => {
  // adjust article count if a featured article exists
  const hasFeatured = items.some(item => item.featuredArticle === true)

  const articleCount = hasFeatured ? items.length - 1 : items.length

  // if all articles are already showing, render nothing
  if (articleCount <= pagination) {
    return null
  }

  return (
    <button
      className={styles.more}
      onClick={() => setPagination(pagination + increment)}
      type="button"
    >
      View More
    </button>
  )
}

const NewsroomPage = props => {
  const [pressPagination, setPressPagination] = useState(PAGINATION_INITIAL.press)
  const [mediaPagination, setMediaPagination] = useState(PAGINATION_INITIAL.media)

  const { data } = props
  const { allContentfulArticle: articles, globalMeta, pageMeta } = data

  const press = []
  const media = []

  articles.nodes.forEach(article => {
    // start with primary category
    let categories = [article.category]

    // push secondary categories if they exist
    if (article.tags) {
      categories.push(...article.tags)
    }

    // convert the categories to lowercase
    categories = categories.map(category => category.toLowerCase())

    // remove duplicates
    categories = categories.filter((category, index) => categories.indexOf(category) === index)

    // check if categorized as press
    if (categories.includes(CATEGORIES.press)) {
      press.push(article)
    }

    // check if categorized as media
    if (categories.includes(CATEGORIES.media)) {
      media.push(article)
    }
  })

  return (
    <>
      <Meta
        base={globalMeta}
        page={{ pageMeta }}
        staticTitle={NEWSROOM.title}
        hubSpotObj={{
          contentTypeKey: PAGE_TRACKER_KEYS.NEWROOM_HOMEPAGE,
          contentTypeValue: PAGE_TRACKER_VALUES.BLOG_POST
        }}
      />

      <Header pageColor={COLORS.FLURO} showGridLines={false} theme={THEMES.LIGHT} />

      <PageSection className={css(styles.pressSection, styles.pageSection)} theme={THEMES.LIGHT}>
        <div className={styles.container}>
          <Headline className={styles.pageHeading} size={3}>
            Newsroom
          </Headline>
          <p className={styles.subhead}>Press Releases</p>

          <PressArticles items={press} pagination={pressPagination} />

          <Pagination
            increment={PAGINATION_INCREMENT.press}
            items={press}
            pagination={pressPagination}
            setPagination={setPressPagination}
          />
        </div>
      </PageSection>

      <PageSection className={css(styles.mediaSection, styles.pageSection)} theme={THEMES.TIN}>
        <div className={styles.container}>
          <p className={styles.subhead}>Media Coverage</p>

          <MediaArticles items={media} pagination={mediaPagination} />

          <Pagination
            increment={PAGINATION_INCREMENT.media}
            items={media}
            pagination={mediaPagination}
            setPagination={setMediaPagination}
          />
        </div>
      </PageSection>

      <GetInTouch color={COLORS.FLURO} theme={THEMES.DARK} />
      {/* Don't remove link below, it's purpose is to improve SEO */}
      <a style={{ visibility: "hidden" }} href="/resources/all">
        All
      </a>
      <Footer />
    </>
  )
}

export const pageQuery = graphql`
  {
    allContentfulArticle(sort: { order: DESC, fields: date }) {
      nodes {
        category
        date
        featuredArticle
        id
        slug
        source
        thumbnail {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          fixed(quality: 100, height: 35) {
            ...GatsbyContentfulFixed_withWebp_noBase64
          }
          title
        }
        tags
        title
      }
    }
    globalMeta: contentfulPageMeta(id: { eq: "423babd8-0278-5b2e-85f6-77fb7ee55bee" }) {
      ...GlobalMetaData
    }
    pageMeta: contentfulPageMeta(contentful_id: { eq: "3MbI0wssaoob55NVDlbWHQ" }) {
      ...PageMetaData
    }
  }
`

export default NewsroomPage
